import React, { useRef, useEffect, useState } from "react";

// Browser compatibility check
const isBrowserCompatible = () => {
  const ua = navigator.userAgent;
  const isIE = ua.indexOf("MSIE ") > -1 || ua.indexOf("Trident/") > -1;
  const isOldEdge =
    ua.indexOf("Edge/") > -1 && parseInt(ua.split("Edge/")[1]) < 79;

  const hasModernFeatures =
    typeof window.Promise !== "undefined" &&
    typeof window.fetch !== "undefined" &&
    typeof window.URL !== "undefined";

  return !isIE && !isOldEdge && hasModernFeatures;
};

interface VimeoPlayerProps {
  videoId: string;
  onPlay: () => void;
  onError: () => void;
  onProgress: (percent: number) => void;
  title?: string;
  style?: React.CSSProperties;
  playerRef?: React.MutableRefObject<HTMLVideoElement | null>;
}

/**
 * VimeoPlayer — uses a native <video> element with a direct MP4 URL
 * for reliable autoplay on all platforms including iOS Safari & Chrome.
 */
export function VimeoPlayer({
  videoId,
  onPlay,
  onError,
  onProgress,
  title = "Vimeo Video",
  style = { padding: "56.25% 0 0 0", position: "relative" },
  playerRef,
}: VimeoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isCompatible, setIsCompatible] = useState(true);

  // Keep stable references to callbacks
  const onPlayRef = useRef(onPlay);
  const onErrorRef = useRef(onError);
  const onProgressRef = useRef(onProgress);
  onPlayRef.current = onPlay;
  onErrorRef.current = onError;
  onProgressRef.current = onProgress;

  useEffect(() => {
    setIsCompatible(isBrowserCompatible());

    if (!isBrowserCompatible()) {
      console.warn(
        "Browser may not fully support the video player. Please update to a modern browser."
      );
      onErrorRef.current();
      return;
    }

    const video = videoRef.current;
    if (!video) return;

    if (playerRef) playerRef.current = video;

    let destroyed = false;
    let playFired = false;
    let fallbackTimer: ReturnType<typeof setTimeout> | null = null;

    const handlePlay = () => {
      if (!destroyed && !playFired) {
        playFired = true;
        if (fallbackTimer) clearTimeout(fallbackTimer);
        console.log("[Vimeo] Play event fired");
        onPlayRef.current();
      }
    };

    const handleError = () => {
      if (!destroyed) {
        console.error("[Vimeo] Video error:", video.error);
        onErrorRef.current();
      }
    };

    const handleTimeUpdate = () => {
      if (!destroyed && video.duration > 0) {
        const percent = (video.currentTime / video.duration) * 100;
        onProgressRef.current(percent);
      }
    };

    // Fallback: programmatically play() when the video has enough data.
    // Chrome on iOS may block the `autoplay` HTML attribute on dynamically
    // mounted elements but still allow a programmatic muted play().
    const handleCanPlay = () => {
      if (!destroyed && video.paused) {
        console.log("[Vimeo] canplay fired, attempting programmatic play()");
        video.play().catch((err) => {
          console.warn("[Vimeo] Programmatic play() blocked:", err);
        });
      }
    };

    // Last resort: if video data loaded but play never fires,
    // treat as success after 2s (video loaded = provider works).
    const handleLoadedData = () => {
      if (!destroyed && !playFired) {
        console.log("[Vimeo] loadeddata fired, starting fallback timer");
        fallbackTimer = setTimeout(() => {
          if (!destroyed && !playFired) {
            console.log("[Vimeo] Fallback → treating loaded video as success");
            playFired = true;
            onPlayRef.current();
          }
        }, 2000);
      }
    };

    video.addEventListener("play", handlePlay);
    video.addEventListener("error", handleError);
    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("canplay", handleCanPlay);
    video.addEventListener("loadeddata", handleLoadedData);

    return () => {
      destroyed = true;
      if (fallbackTimer) clearTimeout(fallbackTimer);
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("error", handleError);
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("canplay", handleCanPlay);
      video.removeEventListener("loadeddata", handleLoadedData);
      if (playerRef) playerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isCompatible) {
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
        <p className="text-yellow-800">
          Your browser may not fully support the video player. Please update to
          a modern browser like Chrome, Firefox, Safari, or Edge.
        </p>
      </div>
    );
  }

  return (
    <div style={{ ...style, position: "relative", overflow: "hidden" }}>
      {/* CSS to hide native video controls/play button overlay */}
      <style>{`
        .vimeo-native-video::-webkit-media-controls {
          display: none !important;
        }
        .vimeo-native-video::-webkit-media-controls-start-playback-button {
          display: none !important;
        }
        .vimeo-native-video::-webkit-media-controls-play-button {
          display: none !important;
        }
      `}</style>
      <video
        ref={videoRef}
        className="vimeo-native-video"
        src={videoId}
        title={title}
        autoPlay
        muted
        loop
        playsInline
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          height: "100%",
          width: "100%",
          objectFit: "cover",
          pointerEvents: "none",
        }}
      />
    </div>
  );
}
